import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs';

import { TOOL_CHANGED_EVENT, type Tool, type ToolChangedDetail } from './tool.types.js';
import { TOOL_TRANSITIONS, type ToolFsmEvent, type ToolState } from '../fsm/tool.fsm.js';
import { FSM } from '../fsm/fsm.js';
import type { WhiteboardBus } from '../bus/whiteboard-bus.js';
import type { UiAction } from '../bus/ui-action.events.js';

/**
 * Owns the current tool mode (TR16).
 *
 * Side effects on change:
 *   1. Drives the tool-FSM (`core/fsm/tool.fsm.ts`) so all transitions are
 *      validated against the static transition table.
 *   2. Reflects the tool onto the host's `data-tool` attribute for CSS.
 *   3. Dispatches a bubbling `tool-changed` CustomEvent for legacy listeners.
 *   4. Emits a `drag/end` UiAction on the bus so the InteractionController can
 *      escape any in-flight drag (cross-FSM safety net F12 from TD-001 §14;
 *      loose-coupling — no direct reference to InteractionController).
 *
 * If a `WhiteboardBus` is supplied the controller also subscribes to
 * `tool/set` UiActions so the toolbar can drive tool changes via the bus
 * (TR12) instead of calling `setTool()` directly.
 */
export class ToolController implements ReactiveController {
    private readonly host: ReactiveControllerHost & HTMLElement;
    private readonly bus: WhiteboardBus | undefined;
    private readonly fsm = new FSM<ToolState, ToolFsmEvent>('select', TOOL_TRANSITIONS);
    private readonly disconnect$ = new Subject<void>();
    private _tool: Tool = 'select';
    private _locked = false;

    constructor(host: ReactiveControllerHost & HTMLElement, bus?: WhiteboardBus) {
        this.host = host;
        this.bus = bus;
        host.addController(this);
    }

    hostConnected(): void {
        this.reflect();
        const bus = this.bus;
        if (!bus) return;
        bus.uiActions$
            .pipe(
                filter((a: UiAction): a is Extract<UiAction, { type: 'tool/set' }> =>
                    a.type === 'tool/set',
                ),
                takeUntil(this.disconnect$),
                takeUntil(bus.destroy$),
            )
            .subscribe({
                next: (a) => this.applyTool(a.tool, /* emitDragEnd */ true),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[ToolController] bus error swallowed');
                    }
                },
            });
    }

    hostDisconnected(): void {
        this.disconnect$.next();
        this.disconnect$.complete();
    }

    get tool(): Tool {
        return this._tool;
    }

    /** Current tool-FSM state — exposed for tests and status indicators. */
    get fsmState(): ToolState {
        return this.fsm.current;
    }

    setTool(tool: Tool): void {
        this.applyTool(tool, /* emitDragEnd */ true);
    }

    /** Called after a one-shot tool successfully placed its object. */
    resetToSelect(): void {
        this.setTool('select');
    }

    /** Forces tool to `select` and ignores subsequent tool changes (PRD-X TR5). */
    lockToSelect(): void {
        this._locked = false;
        this.applyTool('select', /* emitDragEnd */ true);
        this._locked = true;
    }

    /** Re-enables tool changes after a previous {@link lockToSelect}. */
    unlockTool(): void {
        this._locked = false;
    }

    private applyTool(tool: Tool, emitDragEnd: boolean): void {
        if (this._locked) return;
        if (tool === this._tool) return;
        const event = `set/${tool}` as ToolFsmEvent;
        if (this.fsm.canSend(event)) {
            this.fsm.send(event);
        }
        this._tool = tool;
        this.reflect();
        this.host.dispatchEvent(
            new CustomEvent<ToolChangedDetail>(TOOL_CHANGED_EVENT, {
                detail: { tool },
                bubbles: true,
                composed: true,
            }),
        );
        this.host.requestUpdate();
        if (emitDragEnd) {
            this.bus?.uiActions$.next({ type: 'drag/end', worldX: 0, worldY: 0 });
        }
    }

    private reflect(): void {
        this.host.dataset['tool'] = this._tool;
    }
}
